// docLinkify — the workspace's .md/.pdf references become doors to the reader.
//
// Part of the P2 doc-editor build (DOC-EDIT-CONTRACT.md, Frontend
// linkification (C)). Kit's replies cite workspace paths constantly —
// "SYSTEM/kit-os/mirror/doc-sync.py.md" style paths — and every one of those
// is a document the reader at /docs already serves.
//
// Three pieces, per the frozen contract:
//
//   normalizeDocRef(s)  — clean up the messy form a reference arrives in
//                         (absolute /Users/... path, ./ prefix, file: URI,
//                         ":12" line anchor, trailing punctuation from being
//                         cited at the end of a sentence) into the
//                         workspace-relative path the doc index keys on.
//   openInDocs(docRef)  — resolve against the index (cached 5s) and open the
//                         reader in a NEW browser tab. Unresolvable refs open
//                         the docs SEARCH pre-filled, so a dead reference
//                         still lands somewhere useful.
//   scanDocRefs(text)   — find path-like tokens in plain chat/list text:
//                         contains a slash, ends .md/.pdf, no spaces, never
//                         inside http(s) URLs or email addresses, with length
//                         guards. Bare names like "SKILL.md" count too, when
//                         the token has no spaces.
//
// Resolution NEVER slugifies client-side: ids are produced by doc-sync.py and
// the server's GET /api/docs/index is the only source of truth (path → id).

import { fetchDocIndex } from "./ccApi";

// ── index cache (5s TTL per the contract) ──────────────────────────────────

let _indexCache = null; // { map: {path: id}, at: epochMs }

const INDEX_TTL_MS = 5000;

// Exported for tests only: drop the cache so a test starts fresh.
export const _resetDocIndexCache = () => {
  _indexCache = null;
};

async function loadIndex() {
  if (_indexCache && Date.now() - _indexCache.at < INDEX_TTL_MS) {
    return _indexCache.map;
  }
  const body = await fetchDocIndex();
  // The endpoint returns { index: {path: id, ...} } today; accept the bare
  // map too, so a backend shape extension ("extend only") can't break us.
  const map = body && body.index ? body.index : body || {};
  _indexCache = { map, at: Date.now() };
  return map;
}

// ── normalizeDocRef ─────────────────────────────────────────────────────────

// The workspace root as an absolute path (Kit cites these from the Mac side).
export const WORKSPACE_PREFIX = "/Users/socializerender/.openclaw/workspace/";

// Trailing characters a reference picks up from prose: closing quote, closing
// paren/bracket, sentence punctuation, a comma in a list. Stripped repeatedly
// so `"SYSTEM/X.md".` and `(SYSTEM/X.md)` both come clean.
const TRAILING_PUNCT = /[)\]}'".,;:!?\u201d\u2019]+$/;
// A leading quote or bracket the token was wrapped in. NOT a leading "./" or
// "/" (those come after) and never strips path characters.
const LEADING_PUNCT = /^[(\['"\u201c\u2018]+/;

// `file:///Users/.../X.md` and `file://X.md` — strip the scheme first; the
// double leading slashes it leaves are collapsed to one so the workspace
// prefix check below still sees a normal absolute path.
const LINE_ANCHOR = /:\d+$/;

/**
 * The last path segment — what the docs search is pre-filled with when a
 * reference can't be resolved to a document id.
 */
const basenameOf = (p) => {
  const parts = String(p).split("/");
  return parts[parts.length - 1] || p;
};

/**
 * Clean a raw doc reference into the workspace-relative path (or bare
 * SKILL.md-style name) the doc index can key on. Returns "" for input that
 * carries no plausible path (empty after stripping, or containing
 * whitespace).
 */
export function normalizeDocRef(s) {
  if (typeof s !== "string") return "";
  let t = s.trim();
  if (!t) return "";
  // Peel one layer of wrapping punctuation repeatedly before the line-anchor
  // strip — `:12` sits UNDER a closing quote/paren/period, and stripping it
  // first only works when nothing wraps it ('(docs/x.md:12).').
  for (let i = 0; i < 4; i += 1) {
    const before = t;
    t = t
      .replace(LEADING_PUNCT, "")
      .replace(TRAILING_PUNCT, "")
      .replace(LINE_ANCHOR, "");
    if (t === before) break;
  }
  // file: scheme ("file:///Users/…/x.md" → "//Users/…/x.md") — the scheme's
  // `//` is collapsed so the workspace-prefix check below sees a normal
  // absolute path.
  t = t.replace(/^file:\/+/, "/");
  // Absolute workspace prefix → workspace-relative.
  if (t.startsWith(WORKSPACE_PREFIX)) {
    t = t.slice(WORKSPACE_PREFIX.length);
  }
  // Leading ./ (repeated) or absolute /.
  t = t.replace(/^\.\/+/, "").replace(/^\/+/, "");
  // Quotes/brackets the token was wrapped in; repeat in case the anchor
  // stripping revealed another one ("(SYSTEM/X.md):12" → "(SYSTEM/X.md)" → …).
  for (let i = 0; i < 3; i += 1) {
    const before = t;
    t = t.replace(LEADING_PUNCT, "").replace(TRAILING_PUNCT, "");
    if (t === before) break;
  }
  t = t.trim();
  if (!t) return "";
  // A doc ref is a single token — spaces mean it was never a path.
  if (/\s/.test(t)) return "";
  // Length guard: a real path is short; a mangled paste is not.
  if (t.length > 256) return "";
  return t;
}

/**
 * The last path segment — the basename — used to pre-fill the docs search
 * when a reference cannot be resolved to a document id.
 */
export function docRefBasename(s) {
  const norm = normalizeDocRef(s);
  return basenameOf(norm || String(s || ""));
}

// ── scanDocRefs ─────────────────────────────────────────────────────────────

// An "http(s)://…" URL: everything up to whitespace or a quote. ftp/wss too —
// the guard's job is to never link a fragment inside any scheme'd URL.
const URLISH = /\b(?:https?|ftps?|wss?):\/\/\S+/gi;
// An email address: local@domain.tld.
const EMAILISH = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

// The token itself: path-like (may start with ./ or /), no whitespace, ends
// in .md or .pdf. Two shapes:
//   1. contains a "/" — a real path ("SYSTEM/X.md", "docs/../y.pdf")
//   2. bare name, no slash — "SKILL.md"-style ("SKILL.morning-brief.md",
//      "readme.md"); length-guarded below.
// The alternation is ordered so the slash shape wins when both could match
// (the greedy [\w./~-]+ before the slash pulls as much as exists).
const TOKEN = new RegExp(
  [
    // 1: path-ish token with at least one interior segment + .md/.pdf tail
    "(?:\\.{1,2}/)?[A-Za-z0-9~_][A-Za-z0-9~_.+/@-]*\\.pdf|",
    "(?:\\.{1,2}/)?[A-Za-z0-9~_~/][A-Za-z0-9~_.+/-]*/[A-Za-z0-9~_.+/@/-]*\\.(?:md|pdf)|",
    // 2: bare SKILL.md-style token — letters/digits, dots, hyphens; no slash;
    // ends .md/.pdf (the char before the tail may not be a dot, so "a..md"
    // is refused and "e.g" never masquerades)
    "[A-Za-z0-9][A-Za-z0-9~_+-]*(?:\\.[A-Za-z0-9~_+-]+)*\\.(?:md|pdf)",
  ].join(""),
  "gi",
);

/**
 * Find doc references in plain text. Returns an ordered list of
 * { ref, index, length } — ref is the RAW matched token; run
 * normalizeDocRef on it (openInDocs does) before resolving.
 *
 * Guards, per the contract:
 *   - the token must be path-like with a slash, or a bare .md/.pdf name
 *     with no spaces
 *   - never inside an http/https (or ftp/ws) URL or an email address
 *   - length guards: token ≤ 200 chars, text ≤ 200k chars scanned
 */
export function scanDocRefs(text) {
  if (typeof text !== "string") return [];
  if (text.length > 200_000) return []; // bounded scan
  const hits = [];
  const blackout = [];

  // Pass 1: blackout spans (URLs, emails) — a candidate inside one is NOT a
  // doc reference, it is a fragment of the URL/email.
  for (const m of text.matchAll(URLISH)) {
    blackout.push([m.index, m.index + m[0].length]);
  }
  for (const m of text.matchAll(EMAILISH)) {
    blackout.push([m.index, m.index + m[0].length]);
  }
  blackout.sort((a, b) => a[0] - b[0]);
  const inBlackout = (start, end) => {
    let lo = 0;
    let hi = blackout.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const [bs, be] = blackout[mid];
      if (end <= bs) hi = mid - 1;
      else if (start >= be) lo = mid + 1;
      else return true; // overlap of any kind
    }
    return false;
  };

  // Pass 2: candidate tokens.
  for (const m of text.matchAll(TOKEN)) {
    const raw = m[0];
    const start = m.index;
    const end = start + raw.length;
    // Length guards on the token.
    if (raw.length < 5 || raw.length > 200) continue;
    // Bare names must carry the extension as their own tail and read like a
    // filename; a path token needs its slash to have survived. Both already
    // guaranteed by the pattern; the normalized form re-checks, because
    // trailing-quote tokens (e.g. `"x.md"`) are matched including punctuation
    // only when the token is exactly a quoted name — normalizeDocRef cleans.
    const norm = normalizeDocRef(raw);
    if (!norm) continue;
    const hasSlash = norm.includes("/");
    const isBare = !hasSlash && /\.(?:md|pdf)$/.test(norm);
    if (!hasSlash && !isBare) continue;
    if (inBlackout(start, end)) continue;
    hits.push({ ref: raw, index: start, length: raw.length });
  }
  return hits;
}

// ── openInDocs ──────────────────────────────────────────────────────────────

/**
 * Resolve a doc reference against the index (cached, 5s TTL) and open the
 * reader in a NEW browser tab.
 *   found      → window.open("/docs/<id>", "_blank")
 *   not found  → window.open("/docs?q=<basename>", "_blank")
 * `navigate`, when passed, is called instead of window.open (test seam) and
 * the target is returned either way.
 */
export async function openInDocs(docRef, navigate) {
  const ref = normalizeDocRef(docRef);
  if (!ref) return null;
  let id = null;
  try {
    const map = await loadIndex();
    if (map && Object.prototype.hasOwnProperty.call(map, ref)) {
      id = map[ref];
    } else if (map) {
      // A leading "/" survived normalization only in odd cites ("//x/y.md");
      // try the de-slashed form before giving up.
      const alt = ref.replace(/^\/+/, "");
      if (alt !== ref && Object.prototype.hasOwnProperty.call(map, alt)) {
        id = map[alt];
      }
    }
  } catch {
    id = null; // index unreachable — the search fallback is the honest answer
  }
  const target = id
    ? `/docs/${encodeURIComponent(id)}`
    : `/docs?q=${encodeURIComponent(docRefBasename(ref))}`;
  if (typeof navigate === "function") {
    navigate(target);
  } else if (typeof window !== "undefined" && typeof window.open === "function") {
    window.open(target, "_blank");
  }
  return target;
}